import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  API_PATHS,
  type AssignmentDto,
  type CreatePersonBody,
  type PersonDto,
  type SessionUserDto,
  type UpdatePersonBody,
} from '@betreuung/shared';
import { apiFetch } from './http.js';
import { queryKeys } from './queryKeys.js';

/**
 * Mutations.
 *
 * Deliberately not optimistic: the server owns the no-overlap invariant, and a
 * save can legitimately be refused (conflict) or reshape other assignments
 * (clearing a range splits them). Predicting that locally would mean rolling
 * back visible state, so the UI shows a pending button and waits for the answer
 * instead — the round trip is a few milliseconds on the target setup.
 */

function useInvalidateAssignments() {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.assignments.all }),
      client.invalidateQueries({ queryKey: queryKeys.changelog }),
    ]);
  };
}

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; password: string }) =>
      apiFetch<{ user: SessionUserDto }>(API_PATHS.login, {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((body) => body.user),
    onSuccess: (user) => {
      client.setQueryData(queryKeys.me, user);
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(API_PATHS.logout, { method: 'POST' }),
    onSuccess: () => {
      // Clearing the cache alone leaves the mounted `me` observer without data
      // and without a refetch, so the app would hang on its loading state. A
      // reload is also the only way to be certain nothing survives: the event
      // stream closes, and no view keeps rendering data from the old session.
      client.clear();
      window.location.assign('/');
    },
  });
}

export interface CreateAssignmentInput {
  personId: number;
  startSlot: number;
  endSlot: number;
}

export function useCreateAssignment() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: (input: CreateAssignmentInput) =>
      apiFetch<{ assignment: AssignmentDto }>(API_PATHS.assignments, {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((body) => body.assignment),
    onSuccess: invalidate,
  });
}

export interface UpdateAssignmentInput extends CreateAssignmentInput {
  id: number;
  expectedUpdatedAt: number;
}

export function useUpdateAssignment() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateAssignmentInput) =>
      apiFetch<{ assignment: AssignmentDto }>(API_PATHS.assignment(id), {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((response) => response.assignment),
    onSuccess: invalidate,
  });
}

export function useDeleteAssignment() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: true }>(API_PATHS.assignment(id), { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Saving "Nicht zugewiesen" for a range. */
export function useClearRange() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: (input: { startSlot: number; endSlot: number }) =>
      apiFetch<{ affected: number }>(API_PATHS.clearRange, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

function useInvalidatePersons() {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.persons }),
      client.invalidateQueries({ queryKey: queryKeys.changelog }),
      // A colour or name change affects every rendered assignment.
      client.invalidateQueries({ queryKey: queryKeys.assignments.all }),
    ]);
  };
}

export function useCreatePerson() {
  const invalidate = useInvalidatePersons();
  return useMutation({
    mutationFn: (input: CreatePersonBody) =>
      apiFetch<{ person: PersonDto }>(API_PATHS.persons, {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((body) => body.person),
    onSuccess: invalidate,
  });
}

export function useUpdatePerson() {
  const invalidate = useInvalidatePersons();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdatePersonBody & { id: number }) =>
      apiFetch<{ person: PersonDto }>(API_PATHS.person(id), {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((response) => response.person),
    onSuccess: invalidate,
  });
}

export function useDeletePerson() {
  const invalidate = useInvalidatePersons();
  return useMutation({
    mutationFn: (id: number) => apiFetch<{ ok: true }>(API_PATHS.person(id), { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
